@if (sheet()) {
  <aw-bottom-sheet
    presentation="sheet"
    [title]="title()"
    [titleId]="'notifications-sheet-title'"
    [bodyLayout]="'fixed'"
    (closed)="closeSheet()"
  >
    <div class="notification-sheet-content">
      <ng-container *ngTemplateOutlet="notificationContent" />
    </div>
  </aw-bottom-sheet>
} @else {
  <ng-container
    actionIcon="check-check"
    [awTabBarAction]="translations.markAllAsSeen"
    [actionDisabled]="!canMarkAllAsSeen()"
    (actionPressed)="markAllAsSeen()"
  />
  <div class="notification-view">
    <aw-page-header
      [inShell]="true"
      [title]="viewTitle()"
      [subtitle]="viewSubtitle()"
      [viewLabel]="translations.views"
      [titlePlacement]="'top'"
      [user]="currentUser()"
      (viewPressed)="openFilter()"
    />

    <ng-container *ngTemplateOutlet="notificationContent" />
  </div>
}

@if (filterOpen()) {
  <aw-bottom-sheet
    #filterSheet
    titleId="notification-view-title"
    [title]="translations.title"
    (closed)="filterOpen.set(false)"
  >
    <aw-content-block [label]="translations.views">
      @if (activeView() === 'inbox') {
        <aw-content-item
          control="switch"
          icon="check-check"
          [label]="translations.showSeenMessages"
          [checked]="showSeenMessages()"
          (pressed)="toggleSeenMessages()"
        />
      }
      @for (view of viewOptions(); track view.value) {
        <aw-content-item
          control="check"
          [label]="view.label"
          [icon]="view.icon"
          [selected]="activeView() === view.value"
          (pressed)="selectView(view.value)"
        >
          <aw-counter-badge value-trailing class="view-count" variant="neutral" [count]="view.count" />
        </aw-content-item>
      }
    </aw-content-block>
  </aw-bottom-sheet>
}

<ng-template #notificationContent>
  <div
    #container
    class="notification-list-container"
    data-header-scroll-fade
    [@.disabled]="disableAnimation() || reducedMotion"
    (scroll)="onListScroll()"
  >
    <aw-inline-list-search
      [placeholder]="translations.search"
      [currentValue]="searchTerm()"
      (searchChanged)="searchNotifications($event)"
    />
    @if (showLoading()) {
      <aw-list-skeleton />
    } @else {
      @for (section of sections(); track section.id) {
        @let collapsed = collapsedSections().has(section.id) && !searchTerm().trim();
        <section class="notification-section" [class.collapsed]="collapsed">
          <aw-group-header
            class="section-header"
            [collapsible]="true"
            [toggleDisabled]="!!searchTerm().trim()"
            [expanded]="!collapsed"
            [toggleLabel]="section.label"
            [controlsId]="'notification-section-' + section.id"
            (toggled)="toggleSection(section.id)"
          >
            <ng-template #sectionLabel>
              <span class="section-avatar" [class.section-avatar--default]="!section.project">
                @if (section.project; as project) {
                  <aw-project-avatar
                    aria-hidden="true"
                    [project]="project"
                    [showLabel]="false"
                    [size]="'s'"
                    [square]="true"
                  />
                } @else {
                  <i class="aw-2-icn section-avatar-icon" aria-hidden="true" [awIcon]="section.icon"></i>
                }
              </span>
              <span class="section-name aw-2-txt--truncate aw-2-pl--m">{{ section.label }}</span>
              <aw-counter-badge
                class="section-count"
                variant="neutral"
                aria-hidden="true"
                [count]="section.notifications.length"
              />
            </ng-template>
            @if (section.project; as project) {
              <a
                groupHeaderLabel
                class="section-label inbox-project-label"
                [routerLink]="['/projects', project.id]"
                (click)="openProjectFeedback()"
              >
                <ng-container *ngTemplateOutlet="sectionLabel" />
              </a>
            } @else {
              <span groupHeaderLabel class="section-label">
                <ng-container *ngTemplateOutlet="sectionLabel" />
              </span>
            }
            @if (section.hasUnseen) {
              <aw-icon-button
                groupHeaderActions
                class="section-mark-read"
                icon="check-check"
                [style]="'subtle'"
                [ariaLabel]="section.markReadLabel"
                [disabled]="!onlineStatus() || markingAll() || markingSections().has(section.id) || section.pending"
                (click)="markSectionAsSeen(section)"
              />
            }
          </aw-group-header>
          @if (!collapsed) {
            <div class="section-content" [id]="'notification-section-' + section.id" [@expandAnimation]>
              @for (row of section.rows; track row.notification.id; let i = $index) {
                @let isGroupChild = row.groupPosition === 'middle' || row.groupPosition === 'last';
                <div
                  class="notification-day"
                  [class.notification-day--grouped]="!!row.groupPosition"
                  [class.notification-day--group-first]="row.groupPosition === 'first'"
                  [class.notification-day--group-last]="row.groupPosition === 'last'"
                  [attr.data-notification-id]="row.notification.id"
                  [@conversationRow]="isGroupChild ? 'child' : 'row'"
                >
                  @if (i + section.startIndex < 20) {
                    <ng-container *ngTemplateOutlet="notificationItem; context: { $implicit: row, section: section }" />
                  } @else {
                    @defer (when row.notification.id === restoredNotificationId(); on viewport) {
                      <ng-container
                        *ngTemplateOutlet="notificationItem; context: { $implicit: row, section: section }"
                      />
                    } @placeholder {
                      <div class="notification-placeholder" aria-hidden="true"></div>
                    }
                  }
                </div>
              }
            </div>
          }
        </section>
      }
    }

    @if (!sheet() && activeView() === 'inbox' && showSeenMessages() && hasMoreSeenConversations()) {
      <div class="notification-history-footer">
        <aw-button
          variant="tertiary"
          [text]="commonTranslations.loadMore"
          [state]="loadingSeenConversations() ? 'loading' : 'default'"
          [disabled]="!onlineStatus()"
          (onclick)="loadMoreSeenConversations()"
        />
      </div>
    }

    @if (!showLoading() && !loadingSeenConversations() && !visibleNotifications().length) {
      <div class="no-notifications">
        <aw-empty-message icon="bell-off" [text]="translations.noNotifications" />
      </div>
    }
  </div>
  <ng-template #notificationItem let-row let-section="section">
    <aw-notification-item
      [notification]="row.notification"
      [pending]="markingAll() || pendingNotifications().has(row.notification.id) || markingSections().has(section.id)"
      [groupCollapsible]="!!row.groupId"
      [groupExpanded]="!!row.expanded"
      (notificationAction)="onNotificationAction(row.notification, $event, row.actionNotifications)"
      (groupToggled)="toggleConversation(row.groupId)"
      (actionExecuted)="closeSheet()"
    />
  </ng-template>
</ng-template>
